import React from "react"
import { Box, Divider, Typography } from "@mui/material"
import { AppDialog, AppDialogBody, AppDialogHeader, AppValueChip } from "@components"
import { dsColorNew } from "@palette"

const chipSx = { mr: 0.5, mb: 0.5 }

// Titulo del bloque de valores segun lo que liste. Un aviso solo trae una de las
// claves: las de season y duration bajo 'values', y los proveedores sin
// normalizar sueltos en la raiz del detalle.
const VALUE_LABELS = {
	providers: 'Affected suppliers',
	pickup_dates: 'Affected pickup dates',
	lengths_of_rental: 'Affected lengths of rental',
	acriss: 'Ambiguous ACRISS codes',
}

// Config contra la que se comparo. Es lo que convierte la lista en accionable:
// las fechas descartadas solo dicen algo al lado de las seasons que hay puestas.
const CONFIG_LABELS = {
	seasons: 'Configured seasons',
	durations: 'Configured durations',
}

// Contadores del chequeo, en el orden en el que el bucle descarta. Los motivos
// de corte traen el desglose entero; los parciales solo los tres primeros.
const COUNTER_LABELS = {
	candidates: 'Rows evaluated',
	valid: 'Rows used',
	discarded: 'Rows discarded',
	no_competitor: 'No competitor',
	no_net_price: 'No net price',
	no_season: 'Outside seasons',
	no_duration: 'Outside durations',
	no_competitor_selected: 'No competitor selected',
	no_site_price: 'No site price',
	no_compute_row: 'Not computable',
}

// Siempre visibles aunque valgan 0: "0 filas validas" es justo lo que explica un
// motivo de corte. El resto a 0 solo seria ruido.
const ALWAYS_SHOWN_COUNTERS = ['candidates', 'valid', 'discarded']

const formatRange = ({ start, end }) => start === end ? `${start}` : `${start} → ${end}`

function Section({ title, children }) {
	return (
		<Box sx={{ mt: 1.5 }}>
			<Typography sx={{ fontSize: 11, fontWeight: 600, color: dsColorNew.textSubtle, textTransform: 'uppercase', letterSpacing: 0.6 }}>
				{title}
			</Typography>
			<Box sx={{ mt: 0.5 }}>{children}</Box>
		</Box>
	)
}

function Counters({ detail }) {
	const entries = Object.entries(COUNTER_LABELS)
		.filter(([key]) => detail[key] !== undefined && detail[key] !== null)
		.filter(([key]) => detail[key] || ALWAYS_SHOWN_COUNTERS.includes(key))

	if (!entries.length) return null

	return (
		<Section title="Rows">
			{entries.map(([key, label]) => (
				<AppValueChip key={key} sx={chipSx}>{`${label}: ${detail[key]}`}</AppValueChip>
			))}
		</Section>
	)
}

// Un aviso puede traer mas de una lista: el motivo generico sale de un descarte
// mixto y ninguna la explica por si sola, asi que se pintan todas las que haya.
function Values({ detail }) {
	// Los proveedores vienen en la raiz y el resto bajo 'values'.
	const values = detail.values ?? detail
	const totals = detail.total ?? {}

	return Object.keys(VALUE_LABELS).flatMap(key => {
		const raw = values[key]

		// ACRISS_AMBIGUOUS_CATEGORY: {provider: {acriss: {category: [cars]}}}
		// Se aplana a "HERTZ / FFAR → Cat1, Cat2" para que quepan en chips.
		if (key === 'acriss') {
			if (!raw || typeof raw !== 'object') return []
			const flat = Object.entries(raw).flatMap(([provider, acrissMap]) =>
				Object.entries(acrissMap).map(([code, categories]) =>
					`${provider} / ${code} → ${Object.keys(categories).join(', ')}`
				)
			)
			if (!flat.length) return []
			return [
				<Section key={key} title={VALUE_LABELS[key]}>
					{flat.map(item => (
						<AppValueChip key={item} tone="warning" sx={chipSx}>{item}</AppValueChip>
					))}
				</Section>
			]
		}

		if (!raw?.length) return []
		const total = totals[key] ?? raw.length
		return [
			<Section key={key} title={total > raw.length ? `${VALUE_LABELS[key]} (first ${raw.length} of ${total})` : VALUE_LABELS[key]}>
				{raw.map(item => (
					<AppValueChip key={item} tone="warning" sx={chipSx}>{item}</AppValueChip>
				))}
			</Section>
		]
	})
}

function Config({ detail }) {
	const config = detail.config ?? {}

	return Object.keys(CONFIG_LABELS).filter(key => config[key]?.length).map(key => (
		<Section key={key} title={CONFIG_LABELS[key]}>
			{config[key].map(range => (
				<AppValueChip key={formatRange(range)} sx={chipSx}>{formatRange(range)}</AppValueChip>
			))}
		</Section>
	))
}

export default function WarningDetailModal({ row, onClose, getLabel, getMessage, isInfo }) {
	if (!row) return null

	// El orden es el de los chips de la fila, que es el de emision en search-export.
	const reasons = (row.warningReasons ?? []).filter(reason => row.warningDetail?.[reason])

	return (
		<AppDialog open={!!row} onClose={onClose} maxWidth="md">
			<AppDialogHeader
				icon="triangle-exclamation"
				title={`${row.toolName ?? ''} · ${row.idOfficePickupName ?? ''} · ${row.executionTime ?? ''}`}
				onClose={onClose}
			/>
			<AppDialogBody>
			{reasons.map((reason, index) => {
				const detail = row.warningDetail[reason]
				return (
					<Box key={reason}>
						{index > 0 && <Divider sx={{ my: 2, borderColor: dsColorNew.grayBorder }}/>}
						<AppValueChip tone={isInfo(reason) ? 'accent' : 'warning'} title={reason}>
							{getLabel(reason)}
						</AppValueChip>
						<Typography sx={{ mt: 1, fontSize: 14, color: dsColorNew.textDark }}>{getMessage(reason)}</Typography>
						<Values detail={detail}/>
						<Config detail={detail}/>
						<Counters detail={detail}/>
					</Box>
				)
			})}
			</AppDialogBody>
		</AppDialog>
	)
}